import { AgentDefinition } from '../models/agent-definition.model';

/**
 * The five agents the design handoff describes, used until the agents endpoint exists.
 *
 * Only `AgentsService.fetchAgents` reads this, so the endpoint replaces one method and this file
 * goes away with it. Every string is the prototype's own copy — it is the design's wording, and it
 * is what a planner deciding whether to trust an agent will actually read.
 *
 * Two things are deliberately absent although the prototype has them: the `owners` list and the
 * `assignment` input row. The ticket removes the "who gets the alert" block, and listing an input
 * the surface offers no control for would advertise something that is not there.
 */

/** The inputs every anomaly-detection agent takes. Identical across all five in the handoff. */
const DETECTION_INPUTS: AgentDefinition['inputs'] = [
  { name: 'kpis', isRequired: true, detail: 'Which KPIs to watch — set below, at least one' },
  { name: 'sensitivity', isRequired: true, detail: 'Per KPI — very conservative to very sensitive. Default: balanced' },
  { name: 'direction', isRequired: false, detail: 'Per KPI — drops, rises or both. Default: both' },
  { name: 'cadence', isRequired: false, detail: 'Weekly or monthly per customer, daily optional. Default: weekly' },
  { name: 'maxPerDay', isRequired: false, detail: 'How many anomalies to surface a day. Default: 10' },
  { name: 'groupByCause', isRequired: false, detail: 'One alert per root cause instead of per SKUL. Default: on' },
  { name: 'automation', isRequired: false, detail: 'Always ask, act when confident, or act if reversible. Default: always ask' }
];

/** Shared by the three anomaly agents that alert rather than act. */
const ALERTS_ONLY_TRIGGER = 'Runs on the full SKU catalogue each time GAINS finishes processing. Alerts only — it never holds or delays a replenishment order.';

const DETECTION_OPTIONS: AgentDefinition['detectionOptions'] = [
  {
    id: 'alert-on-detection',
    label: 'Alert on detection',
    description: 'Notify the responsible owner when a KPI moves past its sensitivity setting',
    enabledByDefault: true
  },
  {
    id: 'run-after-cycle',
    label: 'Run after every GAINS processing cycle',
    description: 'Trigger on optimization completion — no manual start',
    enabledByDefault: true
  }
];

export function buildAgentDefinitionsFixture(): AgentDefinition[] {
  return [
    {
      id: 'demand-outlier',
      stage: 'demand',
      name: 'Demand Transactions Outlier',
      description: 'Flags demand transactions that fall outside expected pattern before they reach the forecast.',
      longDescription:
        'Demand Transactions Outlier scans demand_trans for records that break pattern — one-off bulk orders, duplicated postings, negative quantities, returns booked as demand — scores each against the item/location history, and tags the outliers so forecasting works from clean history.',
      autonomy: 'automate',
      reversibility: 'reversible',
      // Event-driven only: it runs when demand lands, to clean history before the forecast reads it.
      activations: ['event-driven'],
      isAvailable: false,
      roadmapLabel: 'Coming soon',
      runtime: '1 – 6 min',
      prerequisites: [],
      inputs: DETECTION_INPUTS,
      output: 'Outlier-tagged demand transactions with a score and reason code, plus a cleansed history series for forecasting.',
      defaultCadence: 'weekly',
      kpis: [],
      detectionOptions: [
        {
          id: 'exclude-tagged-rows',
          label: 'Exclude tagged rows from forecast',
          description: 'Cleansed series feeds the next forecast run',
          enabledByDefault: true
        },
        {
          id: 'run-on-history-load',
          label: 'Run on demand history load',
          description: 'Trigger automatically when new demand lands',
          enabledByDefault: true
        }
      ],
      whenToUse: ['Before a forecast cycle', 'After a demand history import or ERP reload', 'When forecast error spikes without a business cause'],
      autonomyBlurb: 'Writes outlier tags and the cleansed series directly. It never deletes or edits the source transaction — the original row stays intact.',
      reversibilityBlurb: 'Every tag is reversible from the activity log — untag a single row, a batch or the whole run, and the forecast series reverts with it.',
      scopeLimits: [
        'Does not delete demand records',
        'Does not change the forecast model or parameters',
        'Does not judge intent — a real bulk order can be flagged; review before excluding'
      ],
      recentDecisions: [],
      runHistory: [],
      activeSessions: []
    },
    {
      id: 'service-anomaly',
      stage: 'inventory',
      name: 'Service Level Anomaly Detection',
      description: 'Detects service level drift and target changes against the configured baseline.',
      longDescription:
        'Service Level Anomaly Detection watches achieved fill rate and the target it is measured against. Drift is caught two ways — current period against baseline for sudden drops, window against older window for gradual decline — and a target change is compared new against prior, never averaged.',
      autonomy: 'automate',
      reversibility: 'reversible',
      activations: ['on-demand', 'event-driven'],
      isAvailable: true,
      roadmapLabel: 'Available',
      runtime: '45s – 4 min',
      prerequisites: [],
      inputs: DETECTION_INPUTS,
      output: 'A breach list per KPI — achieved, baseline, change, threshold, and a sudden / gradual tag — delivered as an alert to the planner group.',
      trigger: ALERTS_ONLY_TRIGGER,
      defaultCadence: 'weekly',
      kpis: [
        {
          id: 'service-level',
          label: 'Service level / stockout rate',
          description: 'Achieved fill rate — drift against baseline and breach of target',
          enabledByDefault: true,
          defaults: { measure: 'absolute', threshold: 3, baseline: 'weighted', windowLength: 13, windowUnit: 'weeks', direction: 'down', sensitivity: 3 }
        },
        {
          id: 'target-sl-change',
          label: 'Target service level change',
          description: 'New target vs. prior — compared directly, never averaged',
          enabledByDefault: true,
          defaults: { measure: 'absolute', threshold: 2, baseline: 'median', windowLength: 4, windowUnit: 'weeks', direction: 'both', sensitivity: 3 }
        }
      ],
      detectionOptions: DETECTION_OPTIONS,
      whenToUse: ['On every planning cycle, automatically', 'When customers escalate on fill rate', 'After a service level target is re-set'],
      autonomyBlurb: 'Raises alerts against the KPIs you select. It does not stop replenishment, hold orders or overwrite optimizer output — detection and notification only.',
      reversibilityBlurb:
        'Alerts are dismissible per SKUL or in bulk; the breach list is retained as read-only run history. Nothing downstream is changed, so there is nothing to roll back.',
      scopeLimits: [
        'Does not stop or delay replenishment orders',
        'Does not re-run the optimizer or correct the underlying parameter',
        'Does not evaluate business fit — only movement past the sensitivity you set'
      ],
      recentDecisions: [
        { time: '08:05 AM', action: 'Alert', tone: 'yellow', detail: 'DC2 — achieved fill 4.8pp below target, gradual' },
        { time: '7/24/2026', action: 'Dismiss', tone: 'grey', detail: 'DC1 — 3 alerts dismissed as a known promotion effect' }
      ],
      runHistory: [
        {
          id: 'sla-h-02',
          scenario: 'Weekly run · all DCs',
          status: 'in-progress',
          outcome: '2 breaches — DC2 achieved fill −4.8pp gradual, A-class target +2pp.',
          meta: '7/26/2026 · 08:05 AM · system · sla-h-02'
        },
        {
          id: 'sla-h-01',
          scenario: 'Weekly run · all DCs',
          status: 'completed',
          outcome: '3 breaches on DC1 achieved fill; dismissed as a known promotion effect.',
          meta: '7/24/2026 · 08:05 AM · Dana Whitfield · sla-h-01'
        }
      ],
      activeSessions: [{ id: 'sla-dc2', scenario: 'DC2 · trailing 13 weeks', status: 'awaiting', statusLabel: 'Awaiting review', primary: '1,412 SKULs · −4.8 pp' }]
    },
    {
      id: 'inventory-health-anomaly',
      stage: 'inventory',
      name: 'Inventory Health Anomaly Detection',
      description: 'Detects coverage and excess drifting away from policy.',
      longDescription:
        'Inventory Health Anomaly Detection watches how much stock the plan is actually carrying — days or weeks of supply against the policy band, and excess & obsolete value building against the trailing window. Both sudden jumps and slow accumulation are caught and tagged.',
      autonomy: 'automate',
      reversibility: 'reversible',
      activations: ['on-demand', 'event-driven'],
      isAvailable: false,
      roadmapLabel: 'Coming soon',
      runtime: '45s – 4 min',
      prerequisites: [],
      inputs: DETECTION_INPUTS,
      output: 'A breach list per KPI — current value, baseline, change, threshold, and a sudden / gradual tag — delivered as an alert to the planner group.',
      trigger: ALERTS_ONLY_TRIGGER,
      defaultCadence: 'weekly',
      kpis: [
        {
          id: 'supply-days',
          label: 'Days / weeks of supply',
          description: 'Coverage drifting beyond the policy ceiling or floor',
          enabledByDefault: true,
          defaults: { measure: 'relative', threshold: 20, baseline: 'moving-average', windowLength: 8, windowUnit: 'weeks', direction: 'both', sensitivity: 3 }
        },
        {
          id: 'excess-obsolete',
          label: 'Excess & obsolete',
          description: 'E&O value or units rising against the trailing window',
          enabledByDefault: true,
          defaults: { measure: 'relative', threshold: 15, baseline: 'moving-average', windowLength: 13, windowUnit: 'weeks', direction: 'up', sensitivity: 3 }
        }
      ],
      detectionOptions: DETECTION_OPTIONS,
      whenToUse: ['On every planning cycle, automatically', 'Ahead of an inventory or working-capital review', 'After a demand plan revision that stock has not caught up with'],
      autonomyBlurb: 'Raises alerts against the KPIs you select. It does not stop replenishment, hold orders or overwrite optimizer output — detection and notification only.',
      reversibilityBlurb:
        'Alerts are dismissible per SKUL or in bulk; the breach list is retained as read-only run history. Nothing downstream is changed, so there is nothing to roll back.',
      scopeLimits: [
        'Does not stop or delay replenishment orders',
        'Does not re-run the optimizer or correct the underlying parameter',
        'Does not evaluate business fit — only movement past the sensitivity you set'
      ],
      recentDecisions: [
        { time: '06:30 AM', action: 'Alert', tone: 'yellow', detail: 'DC1 — days of supply +22%, sudden; 260 SKULs' },
        { time: '7/23/2026', action: 'Alert', tone: 'yellow', detail: 'EU_Distribution — E&O +18% gradual over 13 weeks' }
      ],
      runHistory: [
        {
          id: 'iha-h-02',
          scenario: 'Weekly run · all DCs',
          status: 'completed',
          outcome: '2 breaches — DC1 coverage +22% sudden, EU E&O +18% gradual.',
          meta: '7/26/2026 · 06:30 AM · system · iha-h-02'
        },
        {
          id: 'iha-h-01',
          scenario: 'Weekly run · all DCs',
          status: 'completed',
          outcome: 'No breaches above threshold.',
          meta: '7/19/2026 · 06:30 AM · system · iha-h-01'
        }
      ],
      activeSessions: []
    },
    {
      id: 'supply-performance-anomaly',
      stage: 'inventory',
      name: 'Supply Performance Anomaly Detection',
      description: 'Detects supplier and lead-time performance drifting away from the planning values.',
      longDescription:
        'Supply Performance Anomaly Detection watches the supply side: actual receipt lead time against the planning value, how often orders have to be expedited, and supplier on-time in-full performance. These drift quietly and show up as service failures weeks later, so each is measured against its own baseline and tagged sudden or gradual.',
      autonomy: 'automate',
      reversibility: 'reversible',
      activations: ['on-demand', 'event-driven'],
      isAvailable: false,
      roadmapLabel: 'Coming soon',
      runtime: '1 – 5 min',
      prerequisites: [],
      inputs: DETECTION_INPUTS,
      output: 'A breach list per KPI — current value, baseline, change, threshold, and a sudden / gradual tag — delivered as an alert to the planner and procurement groups.',
      trigger: ALERTS_ONLY_TRIGGER,
      defaultCadence: 'weekly',
      kpis: [
        {
          id: 'lead-time-drift',
          label: 'Lead time drift',
          description: 'Actual receipt lead time moving away from the planning value',
          enabledByDefault: true,
          defaults: { measure: 'relative', threshold: 25, baseline: 'median', windowLength: 12, windowUnit: 'weeks', direction: 'both', sensitivity: 3 }
        },
        {
          id: 'expedite-frequency',
          label: 'Expedite frequency',
          description: 'Share of orders expedited per period, per supplier or location',
          enabledByDefault: true,
          defaults: { measure: 'relative', threshold: 30, baseline: 'moving-average', windowLength: 8, windowUnit: 'weeks', direction: 'up', sensitivity: 3 }
        },
        {
          id: 'otif-receipts',
          label: 'OTIF receipts',
          description: 'Supplier on-time in-full receipt performance',
          enabledByDefault: true,
          defaults: { measure: 'absolute', threshold: 5, baseline: 'weighted', windowLength: 13, windowUnit: 'weeks', direction: 'down', sensitivity: 3 }
        }
      ],
      detectionOptions: DETECTION_OPTIONS,
      whenToUse: ['On every planning cycle, automatically', 'Before a supplier review or scorecard', 'When service fails without a demand-side cause'],
      autonomyBlurb:
        'Raises alerts against the KPIs you select. It does not stop replenishment, contact suppliers or rewrite planning lead times — detection and notification only.',
      reversibilityBlurb:
        'Alerts are dismissible per supplier or in bulk; the breach list is retained as read-only run history. Nothing downstream is changed, so there is nothing to roll back.',
      scopeLimits: ['Does not stop or delay replenishment orders', 'Does not update the planning lead time — it reports the drift', 'Does not contact suppliers or raise claims'],
      recentDecisions: [
        { time: '07:10 AM', action: 'Alert', tone: 'yellow', detail: 'VN-118 — receipt lead time 21 → 34 days, gradual' },
        { time: '7/22/2026', action: 'Alert', tone: 'yellow', detail: 'DC2 — expedite frequency +34% over 8 weeks' }
      ],
      runHistory: [
        {
          id: 'spa-h-02',
          scenario: 'Weekly run · all suppliers',
          status: 'in-progress',
          outcome: '2 breaches — VN-118 lead time +62% gradual, DC2 expedites +34%.',
          meta: '7/26/2026 · 07:10 AM · system · spa-h-02'
        },
        {
          id: 'spa-h-01',
          scenario: 'Weekly run · all suppliers',
          status: 'completed',
          outcome: '1 breach — OTIF on CN-204 fell 6pp; procurement notified.',
          meta: '7/22/2026 · 07:10 AM · Priya Anand · spa-h-01'
        }
      ],
      activeSessions: [
        {
          id: 'spa-vn118',
          scenario: 'Supplier VN-118 · trailing 12 weeks',
          status: 'awaiting',
          statusLabel: 'Awaiting review',
          primary: 'Lead time +62% · 1,412 SKULs affected'
        }
      ]
    },
    {
      id: 'pipe-detection',
      stage: 'replenishment',
      name: 'PIPE Detection',
      description: 'Finds pipeline inventory that no longer matches reality — phantom, stale or duplicated in-transit.',
      longDescription:
        'PIPE Detection reconciles open purchase and transfer orders against receipts and supplier confirmations to find pipeline that is wrong: orders past due still counted as in-transit, quantities received but never closed, duplicated ASNs, and transfers with no matching shipment. Wrong pipeline suppresses replenishment, so the shortage only surfaces when the stock-out does.',
      autonomy: 'automate',
      reversibility: 'reversible',
      // On-demand only: reconciling the pipeline is an investigation a planner starts deliberately.
      activations: ['on-demand'],
      isAvailable: false,
      roadmapLabel: 'Coming soon',
      runtime: '1 – 5 min',
      prerequisites: [],
      inputs: DETECTION_INPUTS,
      output: 'A pipeline exception list — phantom, stale, duplicate, unmatched — with the corrected on-order position per SKUL.',
      defaultCadence: 'weekly',
      kpis: [],
      detectionOptions: [
        {
          id: 'apply-corrected-position',
          label: 'Apply corrected on-order position',
          description: 'Feed the correction into the next replenishment run',
          enabledByDefault: true
        },
        {
          id: 'run-on-receipt-posting',
          label: 'Run on receipt posting',
          description: 'Trigger automatically when receipts land',
          enabledByDefault: true
        }
      ],
      whenToUse: ['Before each replenishment cycle', 'When stock-outs occur despite healthy on-order', 'After an ERP cutover or interface outage'],
      autonomyBlurb: 'Writes a corrected on-order position used by replenishment. It never closes or cancels the ERP order — that stays a human action.',
      reversibilityBlurb: 'Corrections are reversible per order or per run for 30 days; reverting restores the original on-order position immediately.',
      scopeLimits: [
        'Does not close, cancel or expedite ERP orders',
        'Does not contact suppliers',
        'Does not create replenishment orders — it corrects the position they are calculated from'
      ],
      recentDecisions: [],
      runHistory: [],
      activeSessions: []
    }
  ];
}
